import axios from 'axios';
import authService from './AuthService';

class SplinterlandsService {
  constructor() {
    this._playerData = null;
    this._balances = {
      sps: 0,
      dec: 0,
      voucher: 0,
      credits: 0,
      rental_credits: 0
    };
    this._listeners = [];
    
    // Request deduplication cache
    this._balanceCache = new Map(); // username -> { data, timestamp, pendingRequest }
    this._CACHE_DURATION = 10000; // 10 seconds cache
    
    // Bei Änderungen des Anmeldestatus die Spielerdaten aktualisieren
    authService.subscribe((authData) => {
      if (authData.isLoggedIn) {
        this.fetchPlayerData(authData.currentUser.username);
      } else {
        this._playerData = null;
        this._balances = {
          sps: 0,
          dec: 0,
          voucher: 0,
          credits: 0,
          rental_credits: 0
        };
        this._notifyListeners();
      }
    });
  }
  
  // Get balances with request deduplication
  async getPlayerBalances(username) {
    if (!username) return null;
    
    const cacheKey = username.toLowerCase();
    const cached = this._balanceCache.get(cacheKey);
    
    // Return cached data if fresh
    if (cached && Date.now() - cached.timestamp < this._CACHE_DURATION) {
      return cached.data;
    }
    
    // If request is pending, wait for it
    if (cached && cached.pendingRequest) {
      return cached.pendingRequest;
    }
    
    // Start new request
    const requestPromise = this._fetchBalances(username);
    
    // Store pending request
    this._balanceCache.set(cacheKey, {
      data: cached?.data || null,
      timestamp: cached?.timestamp || 0,
      pendingRequest: requestPromise
    });
    
    try {
      const data = await requestPromise;
      
      // Update cache with fresh data
      this._balanceCache.set(cacheKey, {
        data: data,
        timestamp: Date.now(),
        pendingRequest: null
      });
      
      return data;
    } catch (error) {
      // Clear pending request on error
      if (cached) {
        this._balanceCache.set(cacheKey, {
          data: cached.data,
          timestamp: cached.timestamp,
          pendingRequest: null
        });
      }
      throw error;
    }
  }
  
  async _fetchBalances(username) {
    try {
      const response = await axios.get(`https://api2.splinterlands.com/players/balances?username=${username}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching balances for ${username}:`, error);
      return null;
    }
  }
  
  // Spielerdaten von der Splinterlands API abrufen
  async fetchPlayerData(username) {
    if (!username) return;
    
    try {
      // Spielerprofil abrufen
      const playerResponse = await axios.get(`https://api2.splinterlands.com/players/details?name=${username}`);
      
      if (playerResponse.data && !playerResponse.data.error) {
        this._playerData = playerResponse.data;
        
        // Token-Balances aus den Spielerdaten extrahieren
        // DEC Balance
        if (this._playerData.balance) {
          this._balances.dec = parseFloat(this._playerData.balance);
        }
        
        // Use cached balances request
        const balancesData = await this.getPlayerBalances(username);
        
        if (balancesData) {
          // Balances durchgehen und den State aktualisieren
          balancesData.forEach(token => {
            if (token.token === 'DEC') {
              this._balances.dec = parseFloat(token.balance);
            } else if (token.token === 'SPS') {
              this._balances.sps = parseFloat(token.balance);
            } else if (token.token === 'VOUCHER') {
              this._balances.voucher = parseFloat(token.balance);
            } else if (token.token === 'CREDITS') {
              this._balances.credits = parseFloat(token.balance);
            } else if (token.token === 'RENTAL_CREDITS') {
              this._balances.rental_credits = parseFloat(token.balance);
            }
          });
        }
        
        // SPS Staked abrufen, wenn nicht in den Token-Balances enthalten
        if (this._balances.sps === 0) {
          const stakingResponse = await axios.get(`https://api2.splinterlands.com/players/sps_balances?username=${username}`);
          
          if (stakingResponse.data) {
            // SPS-Balance und Staked SPS zusammenaddieren
            const spsData = stakingResponse.data.find(item => item.player === username);
            if (spsData) {
              this._balances.sps = parseFloat(spsData.balance || 0);
            }
          }
        }
        
        // Listener über die Änderung informieren
        this._notifyListeners();
      }
    } catch (error) {

    }
  }
  
  // Listener für Änderungen registrieren
  subscribe(callback) {
    this._listeners.push(callback);
    
    // Sofort den aktuellen Status mitteilen
    callback({
      playerData: this._playerData,
      balances: this._balances
    });
    
    // Unsubscribe-Funktion zurückgeben
    return () => {
      this._listeners = this._listeners.filter(listener => listener !== callback);
    };
  }
  
  // Alle Listener benachrichtigen
  _notifyListeners() {
    const data = {
      playerData: this._playerData,
      balances: { ...this._balances }
    };
    
    this._listeners.forEach(callback => callback(data));
  }
  
  // Balance manuell aktualisieren
  async refreshBalances() {
    const user = authService.getCurrentUser();
    if (user && user.username) {
      await this.fetchPlayerData(user.username);
    }
  }
  
  // Getter für aktuelle Daten
  getPlayerData() {
    return this._playerData;
  }
  
  getBalances() {
    return { ...this._balances };
  }
  
  // Abfragen des Token-Stands eines beliebigen Accounts
  async getTokenBalance(username, token = 'SPS') {
    try {
      // Use cached balances request
      const balancesData = await this.getPlayerBalances(username);
      
      if (balancesData && Array.isArray(balancesData)) {
        const tokenData = balancesData.find(item => item.token === token);
        if (tokenData) {
          return tokenData.balance;
        }
      }
      
      // Wenn SPS nicht gefunden wurde, versuche auch die SPS-Staking-Balance abzurufen
      if (token === 'SPS') {
        const stakingResponse = await axios.get(`https://api2.splinterlands.com/players/sps_balances?username=${username}`);
        
        if (stakingResponse.data) {
          const spsData = stakingResponse.data.find(item => item.player === username);
          if (spsData) {
            return spsData.balance || '0';
          }
        }
      }
      
      return '0';
    } catch (error) {
      return '0';
    }
  }

  // Speziell für den Bonus Pool DEC-Stand
  async getBonusPoolBalance() {
    try {
      // Use cached balances request
      const balancesData = await this.getPlayerBalances('elmo.pool');
      
      if (balancesData && Array.isArray(balancesData)) {
        const decToken = balancesData.find(item => item.token === 'DEC');
        if (decToken) {
          return parseFloat(decToken.balance) || 0;
        }
      }
      
      return 0;
    } catch (error) {
      console.error('Error fetching bonus pool balance:', error);
      return 0;
    }
  }
}

// Singleton-Instanz exportieren
const splinterlandsService = new SplinterlandsService();
export default splinterlandsService;